import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { useQuery } from '@tanstack/react-query';

/**
 * Resolves which controls/widgets dashboards to show in overlays.
 * Resolution order: user default → personal (user + brand) → brand shared → global.
 */
export function useProfileOverlayPreferences(brandId?: string | null) {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    queryKey: ['profile-overlay-preferences', user?.id, brandId],
    queryFn: async () => {
      if (!user?.id) {
        return { controlsDashboardId: null, widgetsDashboardId: null };
      }
      const uid = user.id;
      const bid = brandId ?? null;

      const [
        userDefaultControlsRes,
        userDefaultWidgetsRes,
        personalControlsRes,
        personalWidgetsRes,
        sharedControlsRes,
        sharedWidgetsRes,
        globalControlsRes,
        globalWidgetsRes,
      ] = await Promise.all([
        supabase
          .from('os_experiments_dashboards')
          .select('id')
          .eq('type', 'controls')
          .is('brand_id', null)
          .eq('user_id', uid)
          .maybeSingle(),
        supabase
          .from('os_experiments_dashboards')
          .select('id')
          .eq('type', 'widgets')
          .is('brand_id', null)
          .eq('user_id', uid)
          .maybeSingle(),
        bid
          ? supabase
              .from('os_experiments_dashboards')
              .select('id')
              .eq('type', 'controls')
              .eq('user_id', uid)
              .eq('brand_id', bid)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        bid
          ? supabase
              .from('os_experiments_dashboards')
              .select('id')
              .eq('type', 'widgets')
              .eq('user_id', uid)
              .eq('brand_id', bid)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        bid
          ? supabase
              .from('os_experiments_dashboards')
              .select('id')
              .eq('type', 'controls')
              .is('user_id', null)
              .eq('brand_id', bid)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        bid
          ? supabase
              .from('os_experiments_dashboards')
              .select('id')
              .eq('type', 'widgets')
              .is('user_id', null)
              .eq('brand_id', bid)
              .maybeSingle()
          : Promise.resolve({ data: null }),
        supabase
          .from('os_experiments_dashboards')
          .select('id')
          .eq('type', 'controls')
          .is('user_id', null)
          .is('brand_id', null)
          .maybeSingle(),
        supabase
          .from('os_experiments_dashboards')
          .select('id')
          .eq('type', 'widgets')
          .is('user_id', null)
          .is('brand_id', null)
          .maybeSingle(),
      ]);

      const controlsDashboardId =
        userDefaultControlsRes.data?.id ??
        personalControlsRes.data?.id ??
        sharedControlsRes.data?.id ??
        globalControlsRes.data?.id ??
        null;
      const widgetsDashboardId =
        userDefaultWidgetsRes.data?.id ??
        personalWidgetsRes.data?.id ??
        sharedWidgetsRes.data?.id ??
        globalWidgetsRes.data?.id ??
        null;

      return { controlsDashboardId, widgetsDashboardId };
    },
    enabled: !!user?.id,
  });

  return {
    controlsDashboardId: data?.controlsDashboardId ?? null,
    widgetsDashboardId: data?.widgetsDashboardId ?? null,
    isLoading,
  };
}
